import React, { useMemo, useCallback } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Row, Col } from "react-bootstrap";

import MultiSelect from "components/Common/MultiSelect";
import { store } from "store";

import "./styles.scss";
import {
  fetchJobsCompaniesFilter,
  fetchJobsOwnersFilter,
  fetchJobsStatusesFilter
} from "store/modules/jobs/actions";
import { connect } from "react-redux";
import { unionBy } from "lodash";
import { useDebouncedCallback } from "use-debounce";

const Filters = ({
  t,
  isLoading,
  filters,
  filter,
  setFilter,
  clearFilter,
  currentUser,
  search,
  setSearch
}) => {
  const optionCompanies = useMemo(
    () =>
      unionBy((filters.companies || []).map(
        ({ key, name, interviews_amount: amount }) => ({
          label: name,
          value: key,
          amount
        })
      ), filter.companies.map(a => ({
        ...a,
        amount: 0
      })), "value"),
    [filter.companies, filters.companies]
  );

  const optionOwners = useMemo(
    () =>
      unionBy((filters.owners || []).map(
        ({ key, full_name: name, interviews_amount: amount }) => ({
          label: name,
          value: key,
          amount
        })
      ), filter.owners.map(a => ({
        ...a,
        amount: 0
      })), "value"),
    [filter.owners, filters.owners]
  );

  const optionStatuses = useMemo(
    () =>
      unionBy((filters.statuses || []).map(({ status, interviews_amount: amount }) => ({
        label: `job.table.${status}`,
        value: status,
        amount
      })), filter.statuses.map(a => ({
        ...a,
        amount: 0
      })), "value"),
    [filter.statuses, filters.statuses]
  );

  const filterCount = useMemo(() => {
    const filterArray = Object.keys(filter).filter(
      item => Array.isArray(filter[item]) && filter[item].length
    );
    return filterArray.length;
  }, [filter]);

  const onChangeText = useDebouncedCallback(val => {
    setFilter({ ...filter, title: val });
  }, 500);

  const handleInputChange = useCallback(e => {
    setSearch(e.target.value);
    onChangeText(e.target.value);
  }, [setSearch, onChangeText]);

  const handleCompaniesSelect = useCallback(newCompanies => {
    setFilter({ ...filter, companies: newCompanies });
  }, [filter, setFilter]);

  const handleOwnersSelect = useCallback(newOwners => {
    setFilter({ ...filter, owners: newOwners });
  }, [filter, setFilter]);

  const handleStatusesSelect = useCallback(newStatuses => {
    setFilter({ ...filter, statuses: newStatuses });
  }, [filter, setFilter]);

  const fetchCompaniesFilter = useCallback(() => {
    store.dispatch(
      fetchJobsCompaniesFilter({ key: currentUser.key, filter })
    );
  }, [currentUser.key, filter]);

  const fetchOwnersFilter = useCallback(() => {
    store.dispatch(
      fetchJobsOwnersFilter({ key: currentUser.key, filter })
    );
  }, [currentUser.key, filter]);

  const fetchStatusesFilter = useCallback(() => {
    store.dispatch(
      fetchJobsStatusesFilter({ key: currentUser.key, filter })
    );
  }, [currentUser.key, filter]);

  return (
    <Row justify="center" className="people__filters">
      <Col md={12}>
        <Row justify="center">
          <Col sm={12} md={7} lg={3}>
            <input
              id="name-search"
              className="form__search-input name-search__input n-font-small jobs"
              placeholder={t("input.searchTitlePlaceholder")}
              value={search}
              onChange={handleInputChange}
              type="text"
            />
          </Col>
          <Col sm={12} md={5} lg>
            <MultiSelect
              options={optionCompanies}
              value={filter.companies}
              handleSelect={handleCompaniesSelect}
              isLoading={isLoading}
              placeholder="select.jobFilterPlaceholder"
              fetchLatest={fetchCompaniesFilter}
              fetchOnOpen
            />
          </Col>
          <Col sm={12} md lg>
            <MultiSelect
              options={optionOwners}
              value={filter.owners}
              handleSelect={handleOwnersSelect}
              isLoading={isLoading}
              placeholder="select.ownerFilterPlaceholder"
              fetchLatest={fetchOwnersFilter}
              fetchOnOpen
            />
          </Col>
          <Col sm={12} md lg>
            <MultiSelect
              options={optionStatuses}
              value={filter.statuses}
              handleSelect={handleStatusesSelect}
              isLoading={isLoading}
              placeholder="select.statusFilterPlaceholder"
              withTranslationLabel
              fetchLatest={fetchStatusesFilter}
              fetchOnOpen
            />
          </Col>
        </Row>
      </Col>
      <Col md={5} className="people__clear">
        <p className="n-font-small n-font-medium-weight n-grey-60">
          {t(`people.table.selected.${filterCount === 1 ? "0" : "1"}`, {
            count: filterCount
          })}
        </p>
        <p className="n-font-small n-font-medium-weight n-grey-60 people__clear-separate">
          |
        </p>
        <button
          type="button"
          className="jobs__clear-filter n-font-small n-font-light n-grey-60"
          onClick={clearFilter}
        >
          <span className="text ">{t("input.clearAll")}</span>
        </button>
      </Col>
    </Row>
  );
};

Filters.defaultProps = {
  filter: {
    title: "",
    companies: [],
    jobs: [],
    owners: [],
    ratings: [],
    statuses: []
  },
  clearFilter: null
};

Filters.propTypes = {
  t: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    companies: PropTypes.arrayOf(PropTypes.any),
    jobs: PropTypes.arrayOf(PropTypes.any),
    owners: PropTypes.arrayOf(PropTypes.any),
    ratings: PropTypes.arrayOf(PropTypes.any),
    statuses: PropTypes.arrayOf(PropTypes.any)
  }).isRequired,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    title: PropTypes.string,
    companies: PropTypes.arrayOf(PropTypes.any),
    jobs: PropTypes.arrayOf(PropTypes.any),
    owners: PropTypes.arrayOf(PropTypes.any),
    ratings: PropTypes.arrayOf(PropTypes.any),
    statuses: PropTypes.arrayOf(PropTypes.any)
  }),
  currentUser: PropTypes.shape({
    key: PropTypes.string
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
  clearFilter: PropTypes.func
};

const mapStateToProps = state => ({
  currentUser: state?.users?.currentUser
});

export default connect(mapStateToProps)(withTranslation()(Filters));
